import api from "../../utils/api";
import {
  getCurrentUser,
  getImpersonationState,
  getUserIdFromAccessToken,
  persistAuthUser,
  USER_ROLE,
} from "../../lib/authSession";

// Keep these endpoints editable in one place in case backend paths differ.
export const GOOGLE_BUSINESS_ENDPOINTS = {
  oauthUrl: "/api/integrations/google/oauth-url/",
  callback: "/api/integrations/google/callback/",
  status: "/api/integrations/google/status/",
  accounts: "/api/integrations/google/accounts/",
  locations: "/api/integrations/google/locations/",
  availableLocations: "/api/integrations/google/available-locations/",
  selectLocations: "/api/integrations/google/select-locations/",
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const pick = (obj, keys, fallback = null) => {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return fallback;
};

const toTimestamp = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return Date.now();
};

export const normalizeGoogleConnection = (raw) => {
  const remoteId = String(pick(raw, ["id", "connection_id", "location_id"], ""));
  const displayName = String(
    pick(raw, ["display_name", "name", "location_name", "title"], "Google Business Location")
  );
  const ratingRaw = Number(pick(raw, ["avg_rating", "rating", "average_rating"], NaN));
  const avgRating = Number.isFinite(ratingRaw)
    ? Math.max(1, Math.min(5, Math.round(ratingRaw * 10) / 10))
    : undefined;

  const locationIdNum = remoteId ? Number(remoteId) : NaN;

  const item = {
    connectionKey: remoteId ? `google-${remoteId}` : `google-${Date.now()}`,
    remoteId,
    platformId: "google",
    connectedAt: toTimestamp(pick(raw, ["connected_at", "created_at", "updated_at"], Date.now())),
    displayName,
  };

  if (Number.isFinite(locationIdNum) && locationIdNum > 0) {
    item.locationId = locationIdNum;
  }
  if (avgRating !== undefined) item.avgRating = avgRating;
  return item;
};

export const beginGoogleOAuth = async () => {
  const data = await api.get(GOOGLE_BUSINESS_ENDPOINTS.oauthUrl, { auth: true });
  const oauthUrl = pick(data, ["url", "auth_url", "authorization_url"], "");
  return oauthUrl;
};

/** OAuth codes are single-use; dedupe parallel/StrictMode duplicate callback calls. */
const inflightOAuthCallbacks = new Map();

export const completeGoogleOAuth = async ({ code, state }) => {
  const safeCode = code || "";
  const safeState = state || "";
  const dedupeKey = `${safeCode}|${safeState}`;

  if (inflightOAuthCallbacks.has(dedupeKey)) {
    return inflightOAuthCallbacks.get(dedupeKey);
  }

  const qp = new URLSearchParams({ code: safeCode, state: safeState });
  const promise = api
    .get(`${GOOGLE_BUSINESS_ENDPOINTS.callback}?${qp.toString()}`, {
      auth: true,
    })
    .finally(() => {
      inflightOAuthCallbacks.delete(dedupeKey);
    });

  inflightOAuthCallbacks.set(dedupeKey, promise);
  return promise;
};

export const fetchGoogleIntegrationStatus = async () => {
  return api.get(GOOGLE_BUSINESS_ENDPOINTS.status, { auth: true });
};

export const fetchGoogleAccounts = async (integrationId) => {
  if (!integrationId) return [];
  const data = await api.get(
    `${GOOGLE_BUSINESS_ENDPOINTS.accounts}${integrationId}/`,
    { auth: true }
  );
  const accounts = asArray(data?.accounts);
  return accounts.map((account) => ({
    ...normalizeGoogleConnection(account),
    integrationId,
    remoteId: String(account?.id ?? ""),
    accountId: account?.id,
    externalId: account?.external_id,
    displayName: account?.name || "Google Business Account",
  }));
};

/**
 * Ensure the JWT-backed profile is a client with a linked company.
 * Never trust stale company_id from localStorage.
 */
export async function requireClientCompanyContext() {
  const profile = await api.get("/api/users/profile/", { auth: true });

  const tokenUserId = getUserIdFromAccessToken(
    typeof window !== "undefined"
      ? window.localStorage.getItem("repman_access_token")
      : ""
  );

  if (tokenUserId != null && Number(tokenUserId) !== Number(profile.id)) {
    const err = new Error(
      "Your session switched accounts. Please log out and sign in again with your client account."
    );
    err.code = "SESSION_MISMATCH";
    throw err;
  }

  const storedUserId = getCurrentUser()?.id;
  const allowSwitch =
    storedUserId != null && Number(storedUserId) !== Number(profile.id);
  persistAuthUser(profile, { allowUserSwitch: allowSwitch });

  const impersonating = Boolean(getImpersonationState()?.active);
  if (profile.role !== USER_ROLE.CLIENT && !impersonating) {
    const err = new Error(
      "You are signed in as an admin. Log out and sign in with your client account to connect Google."
    );
    err.code = "ADMIN_ON_CLIENT_FLOW";
    throw err;
  }

  const companyId = profile?.company?.id ?? profile?.company_id ?? null;
  if (!companyId) {
    const err = new Error(
      "No company is linked to this account. Log out, sign in with your client user, then try again."
    );
    err.code = "NO_COMPANY";
    throw err;
  }

  return companyId;
}

export const fetchGoogleLocations = async (accountId, companyId) => {
  if (!accountId || !companyId) return [];
  const data = await api.get(
    `${GOOGLE_BUSINESS_ENDPOINTS.locations}${accountId}/`,
    { auth: true }
  );
  const locations = asArray(data?.locations);
  return locations.map((location) => ({
    ...normalizeGoogleConnection(location),
    remoteId: String(location?.id ?? ""),
    locationId: location?.id,
    externalId: location?.external_id,
    displayName: location?.name || "Google Business Location",
  }));
};

/**
 * Fetches accounts then locations. Calls onPhase before each awaited network call
 * so the UI can reflect real progress (no timers).
 * @param {(phase: 'accounts' | 'locations', meta?: { index: number; total: number }) => void} [onPhase]
 */
export const resolveConnectionsFromIntegration = async ({
  integrationId,
  companyId: _legacyCompanyId,
  onPhase,
}) => {
  const resolvedCompanyId = await requireClientCompanyContext();

  onPhase?.("accounts");
  const accounts = await fetchGoogleAccounts(integrationId);

  const expanded = [];
  const total = accounts.length;
  for (let i = 0; i < accounts.length; i += 1) {
    const account = accounts[i];
    onPhase?.("locations", { index: i + 1, total });
    const locations = await fetchGoogleLocations(account.accountId, resolvedCompanyId);
    if (locations.length > 0) {
      expanded.push(
        ...locations.map((location) => ({
          ...location,
          integrationId,
          accountId: account.accountId,
        }))
      );
    } else {
      expanded.push(account);
    }
  }
  return expanded;
};

/**
 * Plan-aware location selection: locations associated with the connected
 * Google email, their connected state, and the caller's plan limit.
 * Response: {connected, provider_email, plan, location_limit, connected_count,
 *            locations: [{id, name, is_connected, ...}], stale}
 */
export const fetchAvailableGoogleLocations = async ({ refresh = false } = {}) => {
  const qs = refresh ? "?refresh=1" : "";
  return api.get(`${GOOGLE_BUSINESS_ENDPOINTS.availableLocations}${qs}`, {
    auth: true,
  });
};

/** Connect exactly these locations (server enforces the plan limit). */
export const selectGoogleLocations = async (locationIds) => {
  return api.post(
    GOOGLE_BUSINESS_ENDPOINTS.selectLocations,
    { location_ids: locationIds },
    { auth: true }
  );
};

/** Map a backend location row to a localStorage connection entry. */
export const connectionFromBackendLocation = (loc, integrationId) => {
  const entry = {
    connectionKey: `google-${loc.id}`,
    remoteId: String(loc.id),
    platformId: "google",
    connectedAt: Date.now(),
    displayName: loc.name || "Google Business Location",
    locationId: loc.id,
  };
  if (integrationId) entry.integrationId = integrationId;
  const rating = Number(loc.platform_average_rating);
  if (Number.isFinite(rating) && rating > 0) {
    entry.avgRating = Math.max(1, Math.min(5, Math.round(rating * 10) / 10));
  }
  return entry;
};

export const connectGoogleBusiness = async () => {
  await requireClientCompanyContext();
  const oauthUrl = await beginGoogleOAuth();
  if (oauthUrl) {
    return { type: "redirect", url: oauthUrl };
  }

  return { type: "error", message: "OAuth URL missing from backend response." };
};

export const disconnectGoogleBusiness = async (connection) => {
  // Backend integration URLs currently expose connect/callback/accounts/locations.
  // No dedicated disconnect endpoint exists yet, so caller should remove local state only.
  return Promise.resolve(connection);
};
